import React, {
  ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Badge, Card, Form, Spinner,
} from 'react-bootstrap';
// @ts-ignore: no def file
import BootstrapTable, { SortOrder, TableChangeState } from 'react-bootstrap-table-next';
import Moment from 'react-moment';
// @ts-ignore: no def file
import { RouteComponentProps } from 'react-router';
// @ts-ignore: no def file
import { Link } from 'react-router-dom';
import { RequestState, useAsyncOperationState } from '../../../../dorian-shared/hooks/useAsyncOperationState';
import { Event } from '../../../../dorian-shared/types/event/Event';
import { api } from '../../../api';
import { DebouncedSearch } from '../../../ui/DebouncedSearch/DebouncedSearch';
import { PageWrapper } from '../../../ui/PageWrapper';
import { DeleteButton } from './DeleteButton';
import { DuplicateButton } from './DuplicateButton';
import { useScrollRestore } from './useScrollRestore';
import './EventListPage.scss';

function getPageLayout(history: RouteComponentProps['history']) {
  return {
    header: {
      title: 'Events Admin Panel',
      settings: 'admin',
    },
    sidebar: {
      nav: [
        {
          title: 'Back',
          href: '/home/',
          action: history.length > 1 && document.referrer !== window.location.href
            ? () => {
              window.history.back();
            } : null,
          variant: 'secondary',
        },
        {
          title: 'Add Event',
          href: '/events/new',
          variant: 'primary',
        },
      ],
    },
  };
}

// --- Formatters ---

const dateTimeFormatter = (value: string) => (
  <Moment
    element="span"
    format="YYYY-MM-DD hh:mm A"
    date={value}
  />
);

export function isEndTimePast(value: string, now = Date.now()): boolean {
  const endTime = Date.parse(value);
  return !Number.isNaN(endTime) && endTime < now;
}

const endTimeFormatter = (value: string) => (
  <span className={isEndTimePast(value) ? 'event-list-end-time--expired' : undefined}>
    {dateTimeFormatter(value)}
  </span>
);

const activeFormatter = (value: boolean) => (
  <Badge variant={value ? 'success' : 'secondary'}>
    {value ? 'Active' : 'Inactive'}
  </Badge>
);

interface Entity {
  id: string | number
}

const createLinkFormatter = (pathPrefix: string, eventListSearch: string) => function linkFormatter(
  value: ReactNode,
  entity: Entity,
) {
  const { id } = entity;
  return (
    <Link
      to={`/${pathPrefix}/${id}${eventListSearch}`}
    >
      {value}
    </Link>
  );
};

// --- Table columns ---

const useColumns = (
  onDeleteSuccess: (eventId: number) => void,
  eventListSearch: string,
) => useMemo(
  () => [
    {
      dataField: 'title',
      text: 'Title',
      formatter: createLinkFormatter('events', eventListSearch),
      sort: true,
    },
    {
      dataField: 'priority',
      text: 'Priority',
      sort: true,
      classes: 'event-list-column--priority',
      headerClasses: 'event-list-column--priority',
    },
    {
      dataField: 'active',
      text: 'Status',
      formatter: activeFormatter,
      classes: 'event-list-column--status',
      headerClasses: 'event-list-column--status',
    },
    {
      dataField: 'startTime',
      text: 'Start Time',
      formatter: dateTimeFormatter,
      sort: true,
      classes: 'event-list-column--date',
      headerClasses: 'event-list-column--date',
    },
    {
      dataField: 'endTime',
      text: 'End Time',
      formatter: endTimeFormatter,
      sort: true,
      classes: 'event-list-column--date',
      headerClasses: 'event-list-column--date',
    },
    {
      dataField: 'updatedAt',
      text: 'Updated',
      formatter: dateTimeFormatter,
      sort: true,
      classes: 'event-list-column--date',
      headerClasses: 'event-list-column--date',
    },
    {
      dataField: 'createdAt',
      text: 'Created',
      formatter: dateTimeFormatter,
      sort: true,
      classes: 'event-list-column--date',
      headerClasses: 'event-list-column--date',
    },
    {
      dataField: 'id',
      text: 'Actions',
      formatter: (_eventId: number, row: Event) => (
        <div className="d-flex">
          <DuplicateButton eventId={row.id} />
          <DeleteButton eventId={row.id} onSuccess={onDeleteSuccess} />
        </div>
      ),
    },
  ],
  [eventListSearch, onDeleteSuccess],
);

// --- List state and pure helpers (exported for testing) ---

export type ActiveFilter = 'all' | 'active' | 'inactive';

export type SearchableEventField = keyof Pick<Event, 'title' | 'description' | 'buttonText' | 'buttonTarget' | 'label' | 'authorLabel' | 'specialRequirements'>;

type SortField = 'title' | 'priority' | 'startTime' | 'endTime' | 'updatedAt' | 'createdAt';

export interface EventListQuery {
  activeFilter: ActiveFilter;
  titleSearch: string;
  sortField: SortField;
  sortOrder: SortOrder;
}

const defaultEventListQuery: EventListQuery = {
  activeFilter: 'all',
  titleSearch: '',
  sortField: 'priority',
  sortOrder: 'desc',
};

const sortFields: SortField[] = ['title', 'priority', 'startTime', 'endTime', 'updatedAt', 'createdAt'];
const searchableEventFields: SearchableEventField[] = [
  'title',
  'description',
  'buttonText',
  'buttonTarget',
  'label',
  'authorLabel',
  'specialRequirements',
];

export function parseEventListQuery(search: string): EventListQuery {
  const params = new URLSearchParams(search);
  const activeFilter = params.get('status');
  const sortField = params.get('sort');
  const sortOrder = params.get('order');

  return {
    activeFilter: activeFilter === 'active' || activeFilter === 'inactive' ? activeFilter : 'all',
    titleSearch: params.get('search') || '',
    sortField: sortFields.includes(sortField as SortField)
      ? sortField as SortField : defaultEventListQuery.sortField,
    sortOrder: sortOrder === 'asc' || sortOrder === 'desc' ? sortOrder : defaultEventListQuery.sortOrder,
  };
}

export function serializeEventListQuery(query: EventListQuery): string {
  const params = new URLSearchParams();
  if (query.activeFilter !== defaultEventListQuery.activeFilter) params.set('status', query.activeFilter);
  if (query.titleSearch) params.set('search', query.titleSearch);
  if (query.sortField !== defaultEventListQuery.sortField) params.set('sort', query.sortField);
  if (query.sortOrder !== defaultEventListQuery.sortOrder) params.set('order', query.sortOrder);
  const search = params.toString();
  return search ? `?${search}` : '';
}

export function applyFilters(
  events: Event[],
  activeFilter: ActiveFilter,
  textSearch: string,
): Event[] {
  const normalizedSearch = textSearch.trim().toLowerCase();
  return events
    .filter((event) => {
      if (activeFilter === 'active') return event.active;
      if (activeFilter === 'inactive') return !event.active;
      return true;
    })
    .filter((event) => !normalizedSearch || searchableEventFields.some(
      (field) => String(event[field] ?? '').toLowerCase().includes(normalizedSearch),
    ));
}

export function sortEvents(events: Event[], sortField: SortField, sortOrder: SortOrder): Event[] {
  const direction = sortOrder === 'asc' ? 1 : -1;
  return [...events].sort((first, second) => {
    const firstValue = first[sortField];
    const secondValue = second[sortField];
    if (typeof firstValue === 'number' && typeof secondValue === 'number') {
      return (firstValue - secondValue) * direction;
    }
    return String(firstValue).localeCompare(String(secondValue)) * direction;
  });
}

// --- Data hook (single responsibility: fetch + local mutations) ---

function useEvents() {
  const [requestState, {
    setToLoading,
    setToError,
    setToSuccess,
  }] = useAsyncOperationState();
  const [events, setEvents] = useState<Event[]>([]);

  useEffect(() => {
    setToLoading();
    api.get('/v1/events')
      .then((response) => {
        setEvents(response.data.events);
      })
      .then(() => setToSuccess())
      .catch(() => setToError());
  }, [setToError, setToLoading, setToSuccess]);

  const deleteEventLocally = useCallback(
    (eventId: number) => setEvents(
      (currentEvents) => currentEvents.filter((event) => event.id !== eventId),
    ),
    [],
  );

  return {
    requestState,
    events,
    deleteEventLocally,
  };
}

// --- Filter controls ---

interface EventListControlsProps {
  activeFilter: ActiveFilter;
  onActiveFilterChange: (filter: ActiveFilter) => void;
  titleSearch: string;
  onTitleSearch: (value: string) => void;
}

function EventListControls({
  activeFilter,
  onActiveFilterChange,
  titleSearch,
  onTitleSearch,
}: EventListControlsProps) {
  return (
    <div className="event-list-controls d-flex align-items-center mb-3 flex-wrap">
      <Form.Group controlId="activeFilter" className="event-list-controls__status mb-0 mr-3">
        <Form.Label className="sr-only">Filter by status</Form.Label>
        <Form.Control
          as="select"
          size="sm"
          value={activeFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            onActiveFilterChange(e.target.value as ActiveFilter);
          }}
          aria-label="Filter by status"
        >
          <option value="all">All events</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </Form.Control>
      </Form.Group>
      <DebouncedSearch
        initialValue={titleSearch}
        onSearchSubmit={onTitleSearch}
        showButton={false}
        placeholder="Search event text"
        className="event-list-controls__search"
      />
    </div>
  );
}

// --- Event list (thin view — delegates logic to hooks) ---

interface EventListProps {
  history: RouteComponentProps['history'];
  location: RouteComponentProps['location'];
}

function EventList({ history, location }: EventListProps) {
  const {
    requestState,
    events,
    deleteEventLocally,
  } = useEvents();

  const query = useMemo(() => parseEventListQuery(location.search), [location.search]);

  const updateQuery = useCallback((updates: Partial<EventListQuery>) => {
    const nextSearch = serializeEventListQuery({ ...query, ...updates });
    if (nextSearch !== location.search) {
      history.replace({
        pathname: location.pathname,
        search: nextSearch,
      });
    }
  }, [history, location.pathname, location.search, query]);

  const onTableChange = useCallback((_type: string, newState: TableChangeState<Event>) => {
    updateQuery({
      sortField: newState.sortField as SortField,
      sortOrder: newState.sortOrder,
    });
  }, [updateQuery]);

  useScrollRestore(requestState === RequestState.Success);

  const columns = useColumns(deleteEventLocally, location.search);

  const filteredEvents = useMemo(
    () => applyFilters(events, query.activeFilter, query.titleSearch),
    [events, query.activeFilter, query.titleSearch],
  );
  const visibleEvents = useMemo(
    () => sortEvents(filteredEvents, query.sortField, query.sortOrder),
    [filteredEvents, query.sortField, query.sortOrder],
  );

  const noDataIndication = () => {
    if (events.length > 0 && visibleEvents.length === 0) {
      return 'No events match the current filters.';
    }
    return 'No events found.';
  };

  switch (requestState) {
    case RequestState.Success:
      return (
        <>
          <EventListControls
            activeFilter={query.activeFilter}
            onActiveFilterChange={(activeFilter) => updateQuery({ activeFilter })}
            titleSearch={query.titleSearch}
            onTitleSearch={(titleSearch) => updateQuery({ titleSearch })}
          />
          <BootstrapTable
            bootstrap4
            keyField="id"
            data={visibleEvents}
            columns={columns}
            sort={{ dataField: query.sortField, order: query.sortOrder }}
            remote={{ sort: true }}
            onTableChange={onTableChange}
            noDataIndication={noDataIndication}
          />
        </>
      );
    case RequestState.Loading:
      return (
        <div className="text-center">
          <Spinner
            variant="primary"
            animation="border"
            className="loadingSpinner justify-content-center"
          />
        </div>
      );
    case RequestState.Error:
      return (
        <div className="text-center">
          Error occurs during events loading. Please try again later or contact support.
        </div>
      );
    default:
      return null;
  }
}

// --- Page component ---

export function EventListPage(props: RouteComponentProps) {
  const {
    history,
    location,
  } = props;
  const pageLayout = getPageLayout(history);

  return (
    <PageWrapper
      {...props}
      page={pageLayout}
    >
      <Card>
        <Card.Body>
          <Card.Title>
            Events
          </Card.Title>
          <EventList history={history} location={location} />
        </Card.Body>
      </Card>
    </PageWrapper>
  );
}
